import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from 'cbc-shared-lib';

@Injectable({
  providedIn: 'root'
})
export class SideMenuService {

  /** 要素権限Map保存用 */
  private _sideMenuAuthorityMap$ = new BehaviorSubject<Map<any, any[]>>(new Map<any, any[]>());

  /** 直近の子要素Map保存用 */
  private _topChildren$ = new BehaviorSubject<Map<any, any[]>>(new Map<any, any[]>());

  /** 非表示対象ボタン一覧Map保存用 */
  private _notViewList$ = new BehaviorSubject<Map<any, string[]>>(new Map<any, string[]>());

  /** TOPタイトルとItemIdのMap保存用 */
  private _titleAndTopItemIdMap$ = new BehaviorSubject<Map<string, string>>(new Map<string, string>());

  /** 要素権限Map用getter */
  get getSideMenuAuthorityMap(): Observable<Map<any, any[]>> {
    return this._sideMenuAuthorityMap$.asObservable();
  }

  /** 直近の子要素Map用getter */
  get getTopChildren(): Observable<Map<any, any[]>> {
    return this._topChildren$.asObservable();
  }

  /** 非表示対象ボタン一覧Map用getter */
  get getNotViewList(): Observable<Map<any, string[]>> {
    return this._notViewList$.asObservable();
  }

  /** TOPタイトルとItemIdのMap用getter */
  get getTitleAndTopItemIdMap(): Observable<Map<string, string>> {
    return this._titleAndTopItemIdMap$.asObservable();
  }

  /**
   * コンストラクタ。
   * @memberof SideMenuService
   */
  constructor(private authService: AuthService) { }

  /**
   * configファイルで定義した要素の権限を加えた情報
   * キー：包括契約や単価表などのTOPの情報
   * 値　：包括契約や単価表などの子要素全て
   */
  private sideMenuAuthorityMap = new Map<any, any[]>();

  /**
   * TOPの情報から見て、直近の子要素を持つ情報
   * キー：包括契約や単価表などのTOPの情報
   * 値　：包括契約や単価表などのTOPの情報から見て、直近の子要素
   */
  private topChildren = new Map<any, any[]>();

  /**
   * TOPの情報ごとの非表示対象ボタン一覧情報
   * キー：包括契約や単価表などのTOPの情報
   * 値　：サイドメニューの非表示対象となったItemIdList
   */
  private notViewList = new Map<any, string[]>();

  /**
   * TOPのタイトルとItemIdのマップ
   * キー：TOPのタイトル
   * 値　：ItemId
   */
  private titleAndTopItemIdMap = new Map<string, string>();

  /** ItemId割り振り用 */
  private count = 0;

  /**
   * サイドメニュー画面の要素情報取得
   *
   * @memberof SideMenuService
   */
  public updateSideMenu(sgkz001dConfig: any[]): void {

    for (const topSideMenu of sgkz001dConfig) {

      let topSideMenuItemId = this.getCount();
      let topSideMenuAuthority: any = {
        itemId: topSideMenuItemId,
        title: topSideMenu.title,
        pathInfo: topSideMenu.pathInfo,
        parentItemId: '',
        isViewFlg: true,
        isChildrenFlg: topSideMenu.children.length > 0
      }

      this.titleAndTopItemIdMap.set(topSideMenu.title, topSideMenuItemId);

      this.createViewList(topSideMenuAuthority, topSideMenu.children, topSideMenuItemId);

      let filterItemIdList = [];
      let sideMenuChildren = this.sideMenuAuthorityMap.get(topSideMenuAuthority);

      for (let i = sideMenuChildren.length - 1; i >= 0; i--) {

        let childrenAuthority = sideMenuChildren[i];

        if (!childrenAuthority.isViewFlg) {
          filterItemIdList.push(childrenAuthority.itemId);
          continue;
        }

        if (childrenAuthority.parentItemId === topSideMenuAuthority.itemId) {

          if (this.topChildren.get(topSideMenuAuthority) === undefined) {
            this.topChildren.set(topSideMenuAuthority, [childrenAuthority]);

          } else {
            let topSideMenuChildren = this.topChildren.get(topSideMenuAuthority);

            topSideMenuChildren.unshift(childrenAuthority);
            this.topChildren.set(topSideMenuAuthority, topSideMenuChildren);
          }
        }

        let ownChildren = this.getChildren([childrenAuthority.itemId], [], sideMenuChildren);
        let flgList = ownChildren.map(child => child.isViewFlg);
        let allMatchFlg = flgList.every(flg => flg === false);

        if (allMatchFlg && flgList.length > 0) {
          filterItemIdList.push(childrenAuthority.itemId);
        }
      }

      if (sideMenuChildren.length === filterItemIdList.length) {
        filterItemIdList.push(topSideMenuItemId);
      }

      this.notViewList.set(topSideMenuAuthority, filterItemIdList);
    }

    this._sideMenuAuthorityMap$.next(this.sideMenuAuthorityMap);
    this._topChildren$.next(this.topChildren);
    this._notViewList$.next(this.notViewList);
    this._titleAndTopItemIdMap$.next(this.titleAndTopItemIdMap);
  }

  /**
   * TOPの情報から見た子要素のMapを作成する。
   *
   * @memberof SideMenuService
   */
  private createViewList(topSideMenu: any, children: any[], parentItemId: string): void {

    for (const child of children) {

      let itemId = child.itemId !== '' ? child.itemId : this.getCount();
      let isViewFlg = child.itemId !== '' ? this.authService.isRolesDisplayAllowed('SGKZ001D', itemId) : true;

      let childrenAuthority: any = {
        itemId: itemId,
        title: child.title,
        pathInfo: child.pathInfo,
        parentItemId: parentItemId,
        isViewFlg: isViewFlg,
        isChildrenFlg: child.children.length > 0
      };

      if (this.sideMenuAuthorityMap.get(topSideMenu) === undefined) {
        this.sideMenuAuthorityMap.set(topSideMenu, [childrenAuthority]);

      } else {
        let sideMenuChildren = this.sideMenuAuthorityMap.get(topSideMenu);
        sideMenuChildren.push(childrenAuthority);
        this.sideMenuAuthorityMap.set(topSideMenu, sideMenuChildren);

      }

      if (child.children.length > 0) {
        this.createViewList(topSideMenu, child.children, itemId);
      }
    }
  }

  /**
   * ItemIdListから見た子要素のListを作成する。
   *
   * @memberof SideMenuService
   */
  private getChildren(itemIdList: string[], childrenAuthorityList: any[], sideMenuChildren: any[]): any[] {

    let nowChildren = childrenAuthorityList;

    let targetChildrenAuthorityList =
      sideMenuChildren
        .filter(authority => itemIdList.includes(authority.parentItemId))
        .map(authority => authority);

    let flgList = targetChildrenAuthorityList.map(child => child.isViewFlg);
    let allMatchFlg = flgList.every(flg => flg === false);

    if (targetChildrenAuthorityList.length > 0 && allMatchFlg) {

      for (let itemId of itemIdList) {
        sideMenuChildren
          .forEach(dto => {
            if (itemId === dto.itemId) {
              dto.isViewFlg = false;
            }
          })
      }

      for (let targetChildrenAuthority of targetChildrenAuthorityList) {
        nowChildren.push(targetChildrenAuthority);
      }

      let nextItemIdList = targetChildrenAuthorityList.map(authority => authority.itemId);
      this.getChildren(nextItemIdList, nowChildren, sideMenuChildren);
    }

    return nowChildren;
  }

  /**
   * ItemId加算用メソッド
   *
   * @memberof SideMenuService
   */
  private getCount(): string {
    this.count++;
    return this.count.toString();
  }
}
